import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { format } from "date-fns";
import { dateWithoutTimezone } from "utils/dateWithoutTimezone";
import SectionWrapper from "../../Widgets/SectionWrapper";
import EditFields from "./EditFields";
import ViewFields from "./ViewFields";
import { MedicalHistoryFormValues } from "./interface";
import useGetMedicalHistory from "hooks/api/client/useGetMedicalHistory";
import useUpsertMedicalHistory from "hooks/api/client/useUpsertMedicalHistory";
import useUpdateAllergies from "hooks/api/client/useUpdateAllergies";
import { useGlobalToast } from "components/GlobalToastProvider";

const EMPTY_VALUES: MedicalHistoryFormValues = {
  allergies: [],
  emergencyRoomVisits: "-",
  visionScreen: "-",
  fallRisk: "-",
  lastDentalExam: null,
  lastWellVisit: null,
};

export const MedicalHistory: React.FC = () => {
  const { id: clientId } = useParams<{ id: string }>();
  const clientIdNum = clientId ? Number(clientId) : null;

  const { medicalHistory, refetchMedicalHistory } = useGetMedicalHistory(clientIdNum);
  const { upsertMedicalHistoryMutation, isUpsertMedicalHistoryLoading } = useUpsertMedicalHistory({
    onSuccess: () => {
      refetchMedicalHistory();
      setEditMode(false);
    },
  });
  
  const { addAllergy, removeAllergy } = useUpdateAllergies();
  const { showToast } = useGlobalToast();
  const [isUpdatingAllergies, setIsUpdatingAllergies] = useState(false);

  const [editMode, setEditMode] = useState(false);
  const [formValues, setFormValues] = useState<MedicalHistoryFormValues>(EMPTY_VALUES);
  const [initialValues, setInitialValues] = useState<MedicalHistoryFormValues>(EMPTY_VALUES);

  useEffect(() => {
    if (medicalHistory) {
      const mappedAllergies = medicalHistory.allergies && medicalHistory.allergies.length
        ? medicalHistory.allergies.map((a: any) => a.allergyType || a.allergy_type).filter(Boolean)
        : [];

      const mapped: MedicalHistoryFormValues = {
        allergies: mappedAllergies,
        emergencyRoomVisits: medicalHistory.emergencyRoomVisits || "-",
        visionScreen: medicalHistory.visionScreenInterventions || "-",
        fallRisk: medicalHistory.fallRisk || "-",
        lastDentalExam: medicalHistory.lastDentalExam ? dateWithoutTimezone(medicalHistory.lastDentalExam) : null,
        lastWellVisit: medicalHistory.lastWellVisit ? dateWithoutTimezone(medicalHistory.lastWellVisit) : null,
      };
      setFormValues(mapped);
      setInitialValues(mapped);
    }
  }, [medicalHistory]);

  const handleChange =
    (field: keyof MedicalHistoryFormValues) =>
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setFormValues((prev) => ({ ...prev, [field]: event.target.value }));
    };

  const handleDateChange =
    (field: keyof MedicalHistoryFormValues) => (value: Date | null) => {
      setFormValues((prev) => ({ ...prev, [field]: value }));
    };

  const handleSave = async () => {
    if (!clientIdNum) return;
    
    setIsUpdatingAllergies(true);
    try {
        const originalAllergies = medicalHistory?.allergies || [];
        const newAllergies = formValues.allergies;
        const originalTypes = originalAllergies.map((a: any) => a.allergyType || a.allergy_type).filter(Boolean);
        
        const toDelete = originalAllergies.filter((a: any) => !newAllergies.includes(a.allergyType || a.allergy_type));
        for (const a of toDelete) {
            await removeAllergy({ id: a.id });
        }

        const toAdd = newAllergies.filter(type => !originalTypes.includes(type));
        for (const type of toAdd) {
            await addAllergy({ clientId: clientIdNum, payload: { allergyType: type } });
        }
    } catch (error: any) {
        const message = error?.response?.data?.error || "Failed to update allergies";
        showToast({ message, errorState: true });
        setIsUpdatingAllergies(false);
        return;
    }
    setIsUpdatingAllergies(false);

    const payload = {
      emergencyRoomVisits: formValues.emergencyRoomVisits === "-" ? null : formValues.emergencyRoomVisits,
      visionScreenInterventions: formValues.visionScreen === "-" ? null : formValues.visionScreen,
      fallRisk: formValues.fallRisk === "-" ? null : formValues.fallRisk,
      lastDentalExam: formValues.lastDentalExam ? format(formValues.lastDentalExam, "yyyy-MM-dd") : null,
      lastWellVisit: formValues.lastWellVisit ? format(formValues.lastWellVisit, "yyyy-MM-dd") : null,
    };

    upsertMedicalHistoryMutation({ clientId: clientIdNum, payload });
  };

  const handleCancel = () => {
    setFormValues(initialValues);
    setEditMode(false);
  };

  const saveDisabled = JSON.stringify(formValues) === JSON.stringify(initialValues) || isUpsertMedicalHistoryLoading || isUpdatingAllergies;

  return (
    <SectionWrapper
      title="Medical History"
      description="A comprehensive overview of the client's health background, including allergies, prior medical events, and routine care, to support informed and safe treatment planning."
      editMode={editMode}
      onEdit={() => setEditMode(true)}
      onCancel={handleCancel}
      onSave={handleSave}
      saveDisabled={saveDisabled}
    >
      {editMode ? (
        <EditFields formValues={formValues} handleChange={handleChange} handleDateChange={handleDateChange} />
      ) : (
        <ViewFields formValues={formValues} />
      )}
    </SectionWrapper>
  );
};

export default MedicalHistory;
